<template>
  <v-card v-if="property" class="pa-6 pa-md-8 trade-attachments mt-6 helmi-card">
    <div class="helmi-card-title">
      <template v-if="id">
        {{ property.address }} {{ property.flat }}
      </template>

      <template v-else>
        Kaupan liitteet
      </template>
    </div>

    <v-list class="mb-0 w-100 borders px-0 mx-n4 mb-0 pb-0">
      <v-list-item
        v-for="attachment in filteredAttachments"
        :key="attachment.id"
        @click="downloadFile(attachment.id, attachment.description)"
      >
        <v-list-item-avatar
          class="mr-4"
          :color="attachment.title === 'Allekirjoitettu kauppakirja' ? 'red' : 'blue'"
        >
          <v-icon dark>
            description
          </v-icon>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>
            {{ attachment.title }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ attachment.description }}
          </v-list-item-subtitle>
        </v-list-item-content>

        <v-list-item-action>
          <v-icon>download</v-icon>
        </v-list-item-action>
      </v-list-item>

      <v-list-item
        v-for="attachment in taxAttachments"
        :key="attachment.id"
        @click="printTaxTrade(attachment.json)"
      >
        <v-list-item-avatar
          class="mr-4"
          color="orange"
        >
          <v-icon dark>
            description
          </v-icon>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>
            Todistus maksetusta varainsiirtoverosta
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ attachment.json.certificate.BuyerName }}
            {{ attachment.json.AcceptedTimestamp | date({ type: 'full' }) }}
          </v-list-item-subtitle>
        </v-list-item-content>

        <v-list-item-action>
          <v-icon>download</v-icon>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <v-row justify="end" class="mt-6">
      <v-btn
        color="primary"
        dark
        rounded
        depressed
        @click="downloadAll()"
      >
        Lataa kaikki

        <v-icon right>
          download
        </v-icon>
      </v-btn>
    </v-row>
  </v-card>

  <v-card v-else-if="!loading && id" class="pa-6 pa-md-10 trade-attachments mt-6">
    <div class="helmi-card-title">
      Linkki ei ole voimassa
    </div>
    <p class="mb-2">
      Pyydä uusi linkki kohteen välittäjältä!
    </p>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { printTaxTradeCertificate } from './taxTransfer/taxTransferCertificate';
import { apiGet } from '@/api';
import download from 'downloadjs'; // eslint-disable-line

export default {
  props: {
    id: {
      type: String,
      default: () => null,
    },

    tradeId: {
      type: String,
      default: () => null,
    },
  },

  data: () => ({
    taxAttachments: [],
    attachments: [],
    property: null,
    loading: true,
  }),

  computed: {
    ...mapGetters({
      userDetails: 'userDetails',
    }),

    filteredAttachments() {
      if (this.userDetails.id) {
        return this.attachments;
      }

      return this.attachments.filter(a => !a.documentType);
    },
  },

  async mounted() {
    let response = {};

    if (this.id) {
      await this.getAttachmentsByHash();

      response = await this.getTaxTransferAttachmentsByHash();
    } else {
      await this.getAttachmentsByTradeId();

      response = await this.getTaxTransferAttachmentsByTradeId();
    }

    if (response.success) {
      this.taxAttachments = response.attachments.map((item) => {
        if (item.json) {
          return {
            ...item,
            type: 'taxTransfer',
            json: JSON.parse(item.json),
          };
        }

        return { ...item, type: 'attachment' };
      });
    }

    this.loading = false;
  },

  methods: {
    async getAttachmentsByTradeId() {
      const data = await apiGet(`dias/trade-documents-by-trade-id/${this.tradeId}`);

      this.property = data.property;

      this.attachments = data.attachments;
    },

    async getAttachmentsByHash() {
      const data = await apiGet(`dias/trade-documents/${this.id}`);

      this.property = data.property;

      this.attachments = data.attachments;
    },

    async getTaxTransferAttachmentsByTradeId() {
      const data = await apiGet(`properties/tax-transfer-attachments-by-trade-id/${this.tradeId}`);

      return data;
    },

    async getTaxTransferAttachmentsByHash() {
      const data = await apiGet(`properties/tax-transfer-attachments/${this.id}`);

      return data;
    },

    async downloadAll() {
      for await (const a of this.filteredAttachments) {
        await this.downloadFile(a.id, a.description);
      }

      for await (const t of this.taxAttachments) {
        await this.printTaxTrade(t.json);
      }
    },

    async downloadFile(id, name) {
      const baseURL = process.env.NODE_ENV === 'production'
        ? '/public/backend/api/v1/'
        : 'http://localhost:8888/public/backend/api/v1/';

      const options = {
        method: 'GET',
        credentials: 'include',
      };

      const endpoint = this.id ? `trade-documents-download/${this.id}` : `trade-documents-download-by-trade-id/${this.tradeId}`;

      const docResponse = await fetch(`${baseURL}dias/${endpoint}/${id}`, options);

      const doc = await docResponse.arrayBuffer();

      download(doc, name, 'application/pdf');
    },

    currency(value) {
      return this.$options.filters.currency(value || 0);
    },

    area(value) {
      return this.$options.filters.area(value);
    },

    printTaxTrade(taxStatus) {
      printTaxTradeCertificate(taxStatus, this.currency, this.area);
    },
  },
};
</script>

<style lang="scss">
.v-sheet.v-card.trade-attachments {
  font-family: Quicksand,sans-serif !important;

  p {
    font-size: 14px;
    margin-bottom: 0;
  }

  .helmi-card-title {
    font-size: 28px;
  }
}
</style>
